/* eslint-disable indent */
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { PostScheduleStatus } from 'src/pages/Facebook/components/types';
import colors from 'src/theme/base/colors';
import { TYPOGRAPHY_WEIGHT } from '../Typography/types';
import { UpdateFacebookPostSchedule } from 'src/pages/Facebook/components/UpdateFacebookPostSchedule';
import { Footer } from '../Footer';
import boxShadows from 'src/theme/base/boxShadows';
import { TableRowComponent } from './TableRow';
import { TableHeadCell } from './TableHeadCell';
import { PostSchedule } from 'src/DTO/FacebookPost.type';
import { ScheduledFacebookPostsProps } from './types';
import { isPostScheduleExpired } from 'src/utils/facebookPostUtils';

export const ScheduledFacebookPosts: React.FC<ScheduledFacebookPostsProps> = ({
  setSchedulePosts,
  scheduledPosts,
  updateScheduledPost,
  handleRestartSchedule,
  getScheduledPostsLoading,
  hasJobWithoutSchedule,
}) => {
  const { lg } = boxShadows;

  const [isScheduleUpdateDialogOpen, setIsScheduleUpdateDialogOpen] = useState<boolean>(false);

  const [selectedPostSchedule, setSelectedPostSchedule] = useState<PostSchedule>();

  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof PostSchedule | undefined>(undefined);

  const handleRequestSort = (property: keyof PostSchedule) => {
    setOrder(prevOrder => (orderBy === property && prevOrder === 'asc' ? 'desc' : 'asc'));
    setOrderBy(property);
  };

  const getScheduledPostPriority = (post: PostSchedule): number => {
    const expired = isPostScheduleExpired(post);
    const inactive = post.status === PostScheduleStatus.INACTIVE;

    if (expired && inactive) return 3;
    if (expired) return 2;
    if (inactive) return 1;
    return 0;
  };

  const isEditable = (post: PostSchedule) =>
    post.status === PostScheduleStatus.INACTIVE && new Date(post.startDate) > new Date();

  const sortData = (array: PostSchedule[], order: 'asc' | 'desc', orderBy?: keyof PostSchedule) => {
    if (!array) return [];

    return [...array].sort((a, b) => {
      if (orderBy === undefined) {
        const aPriority = getScheduledPostPriority(a);
        const bPriority = getScheduledPostPriority(b);
        return aPriority - bPriority;
      }

      const aValue = a[orderBy] ?? '';
      const bValue = b[orderBy] ?? '';

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;

      return 0;
    });
  };

  const getStatus = (post: PostSchedule) => {
    return post?.status === PostScheduleStatus.ACTIVE
      ? PostScheduleStatus.INACTIVE
      : PostScheduleStatus.ACTIVE;
  };

  const handleChange = (FBPost: PostSchedule) => {
    setSchedulePosts(prevDetails => {
      return prevDetails?.map(post =>
        post?.jobId === FBPost?.jobId
          ? {
              ...post,
              status: getStatus(post),
            }
          : post,
      );
    });

    if (FBPost?.jobId) {
      updateScheduledPost({
        variables: {
          scheduledPostData: {
            ...FBPost,
            jobId: FBPost.jobId,
            status: getStatus(FBPost),
          },
        },
      });
    }
  };

  const handleOpenEditor = (post: PostSchedule) => {
    setSelectedPostSchedule(post);
    setIsScheduleUpdateDialogOpen(true);
  };

  if (getScheduledPostsLoading) {
    return (
      <Grid item xs={12}>
        <CircularProgress color="secondary" />
      </Grid>
    );
  }

  const handleRemoveSortFilter = () => {
    setOrderBy(undefined);
    setOrder('asc');
  };

  return (
    <Grid container justifyContent="center">
      {scheduledPosts && scheduledPosts?.length > 0 ? (
        <Grid item xs={12} lg={11} sx={{ marginBottom: '5%' }}>
          <Box mb={3}>
            <Typography variant="h5">
              <Trans>Ütemezett posztok</Trans>
            </Typography>

            {!hasJobWithoutSchedule && (
              <Typography
                variant="body2"
                color="textSecondary"
                fontWeight={TYPOGRAPHY_WEIGHT.LIGHT}
                sx={{
                  mt: 1,
                  fontStyle: 'italic',
                }}>
                <Trans>Jelenleg minden munkához létezik ütemezett Facebook poszt</Trans>
              </Typography>
            )}
          </Box>

          <Box
            mb={5}
            sx={{
              paddingBottom: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <TableContainer
              component={Paper}
              sx={{
                border: `0.5px solid ${colors.inputColors.borderColor.main}`,
                borderRadius: 3,
                padding: 2,
                boxShadow: lg,
              }}>
              <Table size="small">
                <TableHead sx={{ display: 'table-header-group' }}>
                  <TableRow>
                    <TableHeadCell
                      handleRemoveSortFilter={handleRemoveSortFilter}
                      label="Munka neve"
                      columnKey="name"
                      orderBy={orderBy}
                      order={order}
                      handleRequestSort={() => handleRequestSort('name')}
                    />
                    <TableHeadCell
                      handleRemoveSortFilter={handleRemoveSortFilter}
                      label="Város"
                      columnKey="city"
                      orderBy={orderBy}
                      order={order}
                      handleRequestSort={() => handleRequestSort('city')}
                    />
                    <TableHeadCell
                      handleRemoveSortFilter={handleRemoveSortFilter}
                      label="Státusz"
                      columnKey="status"
                      orderBy={orderBy}
                      order={order}
                      handleRequestSort={() => handleRequestSort('status')}
                    />
                    <TableHeadCell
                      handleRemoveSortFilter={handleRemoveSortFilter}
                      label="Gyakoriság"
                      columnKey="frequency"
                      orderBy={orderBy}
                      order={order}
                      handleRequestSort={() => handleRequestSort('frequency')}
                    />
                    <TableHeadCell
                      handleRemoveSortFilter={handleRemoveSortFilter}
                      label="Kezdési dátum"
                      columnKey="startDate"
                      orderBy={orderBy}
                      order={order}
                      handleRequestSort={() => handleRequestSort('startDate')}
                    />
                    <TableHeadCell
                      handleRemoveSortFilter={handleRemoveSortFilter}
                      label="Kezdési idő"
                      columnKey="startTime"
                      orderBy={orderBy}
                      order={order}
                      handleRequestSort={() => handleRequestSort('startTime')}
                    />
                    <TableHeadCell
                      handleRemoveSortFilter={handleRemoveSortFilter}
                      label="Lejárati dátum"
                      columnKey="endDate"
                      orderBy={orderBy}
                      order={order}
                      handleRequestSort={() => handleRequestSort('endDate')}
                    />
                    <TableCell>
                      <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
                        Posztok száma
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
                        Utolsó poszt
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
                        Aktiválás
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
                        Műveletek
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortData(scheduledPosts, order, orderBy).map((row, index) => (
                    <TableRowComponent
                      key={row?.jobId}
                      row={row}
                      index={index}
                      handleChange={() => handleChange(row)}
                      handleOpenEditor={() => handleOpenEditor(row)}
                      isEditable={isEditable}
                      handleRestartSchedule={() => handleRestartSchedule(row?.jobId)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Footer />
        </Grid>
      ) : null}

      {selectedPostSchedule && scheduledPosts && (
        <UpdateFacebookPostSchedule
          setPostDetails={setSchedulePosts}
          selectedPostSchedule={selectedPostSchedule}
          isScheduleUpdateDialogOpen={isScheduleUpdateDialogOpen}
          setIsScheduleUpdateDialogOpen={setIsScheduleUpdateDialogOpen}
          updateScheduledPost={updateScheduledPost}
        />
      )}
    </Grid>
  );
};
